import React, { useMemo } from 'react';
import { Tooltip } from 'primereact/tooltip';

import ResizableDataTable from 'components/ResizableDataTable/ResizableDataTable';
import { FileSharingDetailsModal } from 'components/FileSharing/FileSharingDetailsModal';

import { useListView } from './hooks/useListView';
import getListViewPaginationTemplate from './ListViewPagination';
import ListViewBulkActionsHeader from './ListViewBulkActionsHeader';
import DropzoneOverlay from './DropzoneOverlay';
import { getGlobalColumns } from './listViewGlobalColumnConfigs';
import { getCaseColumns } from './listViewCaseColumnConfigs';
import { isInsideSharedFolder } from 'utils/fileManagerSharedFolderUtils';

const ListView = React.memo(function ListView() {
  const hook = useListView();

  const {
    files,
    selectedRows,
    pageLimit,
    isClientFileManager,
    isGlobalFileManager,
    isFirmDocuments,
    isFileManagerRoute,
    isVirtualFolder,
    getRootProps,
    isDragActive,
    itemsRef,
    showFileSharingDetailsModal,
    setShowFileSharingDetailsModal,
    selectedFile,
    setSelectedFile,
    userContext,
    modals,
    caseDetails,
    breadCrumbs,
    fileManagerFilters,
    handleGetFiles,
    setShowBulkDeleteModal,
    setShowBulkMoveModal,
    setShowShareFileModal,
    setIsCopy,
    onPageChange,
    nextChange,
    previousChange,
    paginationToken,
    previousToken,
    containsFolders,
    downloadFiles,
    markFilesAsRead,
    handleSelectAllCheckboxClick,
    handleSingleCheckboxClick,
    selectAll,
  } = hook;

  const paginatorTemplate = useMemo(
    () => getListViewPaginationTemplate({ pageLimit, onPageChange, nextChange, previousChange, paginationToken, previousToken, files }),
    [pageLimit, onPageChange, nextChange, previousChange, paginationToken, previousToken, files]
  );

  const hideShare = isInsideSharedFolder(fileManagerFilters);

  const header =
    selectedRows.length > 0 ? (
      <ListViewBulkActionsHeader
        isClientFileManager={isClientFileManager}
        isGlobalFileManager={isGlobalFileManager}
        isInsideSharedFolderView={hideShare}
        containsFolders={containsFolders}
        setShowBulkDeleteModal={setShowBulkDeleteModal}
        setShowBulkMoveModal={setShowBulkMoveModal}
        setShowShareFileModal={setShowShareFileModal}
        setIsCopy={setIsCopy}
        onDownload={downloadFiles}
        onMarkAsRead={markFilesAsRead}
        selectedRows={selectedRows}
      />
    ) : (
      false
    );

  const columnParams = useMemo(
    () => ({
      userContext,
      isClientFileManager,
      isGlobalFileManager,
      isFirmDocuments,
      isLeadFileManager: hook.isLeadFileManager,
      modals,
      setSelectedFile,
      itemsRef,
      caseDetails,
      handleGetFiles,
      pageLimit,
      breadCrumbs,
      setShowFileSharingDetailsModal,
      files,
      selectedRows,
      handleSelectAllCheckboxClick,
      handleSingleCheckboxClick,
      selectAll,
    }),
    [
      userContext,
      isClientFileManager,
      isGlobalFileManager,
      isFirmDocuments,
      modals,
      setSelectedFile,
      itemsRef,
      caseDetails,
      handleGetFiles,
      pageLimit,
      breadCrumbs,
      setShowFileSharingDetailsModal,
      files,
      selectedRows,
      handleSelectAllCheckboxClick,
      handleSingleCheckboxClick,
      selectAll,
    ]
  );

  const columns = useMemo(() => {
    return isGlobalFileManager ? getGlobalColumns(columnParams) : getCaseColumns(columnParams);
  }, [isGlobalFileManager, columnParams]);

  const isShowFileSharingDetailsModal = useMemo(() => {
    return (
      showFileSharingDetailsModal &&
      (selectedFile?.lawft_link_expires_on || (selectedFile?.entry_type === 'folder' && selectedFile?.folder_shared === true))
    );
  }, [showFileSharingDetailsModal, selectedFile]);

  const canDrop = !isFileManagerRoute && !isVirtualFolder;

  return (
    <div className="overflow-auto bg-white shadow-middle mb-3" {...(canDrop ? getRootProps() : {})} style={{ position: 'relative' }}>
      <Tooltip target=".share" />
      {canDrop && isDragActive && <DropzoneOverlay />}

      <ResizableDataTable
        header={header}
        value={files}
        responsiveLayout="scroll"
        className="p-datatable-responsive file-sharing-details"
        emptyMessage="No Data Found."
        paginatorTemplate={paginatorTemplate}
        rows={pageLimit}
        stripedRows
        paginator
        columns={columns}
        storageKey="file_manager_list"
      />

      <FileSharingDetailsModal
        show={isShowFileSharingDetailsModal}
        setShow={setShowFileSharingDetailsModal}
        onHide={() => setShowFileSharingDetailsModal(false)}
        selectedFile={selectedFile}
        setSelectedFile={setSelectedFile}
      />
    </div>
  );
});

export default ListView;
